import { apiFetchJson } from "./apiFetchRoute";
import type { LicenseHistoryPage } from "../generated/types";
import type { EnumLicenseAction } from "../generated/enums";

/** Taille de page par défaut. Le serveur plafonne à 200, quoi qu'on demande. */
export const LICENSE_HISTORY_PAGE_SIZE = 50;

/** Critères de l'onglet « Licences ». Un champ vide = filtre inactif. */
export type LicenseHistoryFilters = {
  /**
   * Recherche GLOBALE, faite en SQL côté serveur sur toutes les colonnes textuelles de
   * la ligne (un bloc par mot, tous les mots requis). Pas en mémoire : l'historique est
   * paginé côté serveur, une recherche locale ne verrait que la page affichée.
   */
  q?: string;
  /** Produit visé — égalité exacte (un uuid se copie-colle). */
  uuid?: string;
  action?: EnumLicenseAction | "";
  /** Recherche PARTIELLE : `codes` stocke plusieurs codes séparés par des virgules. */
  code?: string;
  /**
   * Comptes et entreprises se filtrent par ID, jamais par nom : `account_name`,
   * `company_stock_name` et `company_device_name` viennent de l'API USERS (autre base)
   * et n'existent pas en SQL côté BACK. La ligne porte les ids, le clic les envoie.
   */
  idAccount?: number;
  idCompanyStock?: number;
  idCompanyDevice?: number;
  /** Nom du produit : recherche partielle (jointure `device`). */
  deviceName?: string;
  dateFrom?: string; // AAAA-MM-JJ
  dateTo?: string; // AAAA-MM-JJ
};

/**
 * Récupère une page de l'historique des licences (admin/PSC).
 *
 * `?? { total: 0, rows: [] }` : la route répond **204 No Content** quand il n'y a aucune
 * ligne, et `apiFetchJson` renvoie alors `undefined`. Sans ce repli, lire `.rows`
 * lèverait un TypeError — bug déjà rencontré sur `fetchDevices`.
 */
export const fetchLicenseHistory = async (
  filters: LicenseHistoryFilters = {},
  page = 0,
  pageSize = LICENSE_HISTORY_PAGE_SIZE
): Promise<{ page: LicenseHistoryPage; error: string | null }> => {
  try {
    const result = (await apiFetchJson("getAdminLicensehistory", undefined, {
      query: {
        q: filters.q?.trim() || undefined,
        uuid: filters.uuid?.trim() || undefined,
        action: filters.action || undefined,
        code: filters.code?.trim() || undefined,
        id_account: filters.idAccount || undefined,
        id_company_stock: filters.idCompanyStock || undefined,
        id_company_device: filters.idCompanyDevice || undefined,
        device_name: filters.deviceName?.trim() || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        limit: pageSize,
        offset: page * pageSize,
      },
    })) as LicenseHistoryPage | undefined;

    return { page: result ?? { total: 0, rows: [] }, error: null };
  } catch (e) {
    return {
      page: { total: 0, rows: [] },
      error: "license_history_load_error",
    };
  }
};
